declare const window: { App: any, application: any, addEventListener: any, $: any, innerWidth: number, liveViewDebug: boolean, environment: string, sessionStorage: any };


import {
  createContext, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react'

import useToggle from '../hooks/useToggle'
import noop from '../utils/noop'

interface PreviewerContext {
  htmlPreviewRef?: RefObject<HTMLIFrameElement>,
  previewUrl?: string,
  previewHasCustomUrl: boolean,
  setPreviewUrl: (url?: string) => void,
  preview: {
    open: boolean
    toggle: () => void
    reload: () => void
  }
  activeDrawingNo: string | undefined
  activeApplicationId: string | undefined
}

const Context = createContext<PreviewerContext>({
  setPreviewUrl: noop,
  previewUrl: undefined,
  previewHasCustomUrl: false,
  preview: {
    open: false,
    toggle: noop,
    reload: noop
  },
  activeDrawingNo: undefined,
  activeApplicationId: undefined
})

export const usePreviewerContext = (): PreviewerContext => useContext(Context)

const PreviewerContextProvider = ({ children }: any): JSX.Element => {
  const htmlPreviewRef = useRef<HTMLIFrameElement>(null)
  const [activeDrawingNo, setActiveDrawingNo] = useState<string>(window.application?.currentApplication?.get('drawingNumber') || '')
  const [activeApplicationId, setActiveApplicationId] = useState<string>(window.application?.currentApplication?.get('id') || '')

  const [htmlPreviewInfo, setHtmlPreviewInfo] = useState<{ previewUrl: string, hasCustomUrl: boolean }>()


  const getPreviewUrl = () => {
    let previewUrl = `https://mobileclient${window.environment === 'prod' ? '' : '.accept'}.appmachine.com`

    const previewSessionStorage = window.sessionStorage.getItem('preview-test-url')
    if (previewSessionStorage) {
      previewUrl = previewSessionStorage
    }

    if (previewUrl.indexOf('.com') > 0) {
      previewUrl = previewUrl.substring(0, previewUrl.indexOf('.com') + 4);
    }

    if (previewUrl.endsWith('/#/')) {
      previewUrl = previewUrl.replace('/#/', '')
    }

    return {
      previewUrl: previewUrl,
      hasCustomUrl: !!previewSessionStorage
    }
  }


  useEffect(() => {
    // Set initial value for the previewer
    setHtmlPreviewInfo(getPreviewUrl())
  }, [setHtmlPreviewInfo])


  const setPreviewUrl = useCallback((url?: string) => {
    if (url) {
      window.sessionStorage.setItem('preview-test-url', url)
    } else {
      window.sessionStorage.removeItem('preview-test-url')
    }

    setHtmlPreviewInfo(getPreviewUrl())
  }, [setHtmlPreviewInfo])

  // Previewer open or collapsed. 
  const [previewOpen, togglePreview] = useToggle(window.innerWidth > 1400)

  useEffect(() => {
    if (previewOpen) {
      window.$('section#content > > .content').addClass('deviceOn')
    } else {
      window.$('section#content > > .content').removeClass('deviceOn')
    }
  }, [previewOpen])

  // An one time script that should expose a lot of function on the window for the old designer.
  useEffect(() => {

    // receive message from flutter app
    window.addEventListener('message', (event: any) => {

      if (window.liveViewDebug) {
        console.log('fromDevice', event.data)
      }

      if (event.data?.isLiveView && event.data?.type === 'navigateTo') {
        const applicationItemId = event.data.appItemId
        if (!window.$('#appitemId-' + applicationItemId).find('> div').hasClass('selected')) {
          window.$('#appitemId-' + applicationItemId).find('> div >.applicationItemBlockContents').trigger('click', 'liveview');
          window.$('#appitemId-' + applicationItemId).trigger('click', 'liveview');
        }
      }
    })

    const postMessage = (messageObj: any) => {
      messageObj = {
        ...messageObj,
        isLiveView: true
      }

      if (window.liveViewDebug) {
        console.log('toDevice', messageObj)
      }


      htmlPreviewRef.current?.contentWindow?.postMessage(messageObj, '*')
    }

    const getSelectedApplicationItemId = () => {
      if (window.application.currentApplication.has('selectedApplicationItemId')) {
        return window.application.currentApplication.get('selectedApplicationItemId');
      }

      if (window.application.currentApplication.has('mainMenuApplicationItemId')) {
        return window.application.currentApplication.get('mainMenuApplicationItemId');
      }

      return null
    }

    window.App = window.App || {}
    window.App.liveview = window.App.liveview || {}
    window.App.liveview.toDevice = window.App.liveview.toDevice || {}

    window.App.liveview.toDevice.pushApplicationItemChange = (propertyId: any, appItemId: any, propertyName: any, propertyValue: any, propertyType: any) => {
      postMessage({
        type: 'app_item_change',
        appItemId: appItemId,
        propertyId: propertyId,
        propertyName: propertyName,
        newValue: propertyValue,
        propertyType: propertyType
      });
    }

    window.App.liveview.toDevice.reload = () => {
      postMessage({
        type: 'reload'
      });
    }

    window.App.liveview.toDevice.ModifyCustomJs = (a: any, b: any, c: any, d: any, e: any, f: any) => {
      console.log('window.App.liveview.toDevice.ModifyCustomJs', a, b, c, d, e, f)
    }

    window.App.liveview.toDevice.startWithOptions = (drawnumber: any, selectedAppItemId: any, device: any) => {
      //set drawing number
      setActiveDrawingNo(drawnumber)
      setActiveApplicationId(window.application?.currentApplication?.get('id'))

    }

    window.App.liveview.toDevice.showMenuToggle = (a: any, b: any, c: any) => { }

    window.App.liveview.toDevice.appReload = () => {
      postMessage({
        type: 'reload'
      });
    }

    window.App.liveview.toDevice.navigateTo = (guid: string) => {
      if (!guid) {
        guid = getSelectedApplicationItemId();
      }

      if (guid) {
        postMessage({
          type: 'route',
          appItemId: guid
        });
      }
    }

    window.App.liveview.toDevice.newTree = (newTree: any) => {
      postMessage({
        type: 'new_tree',
        tree: newTree
      });
    }

    window.App.liveview.toDevice.showRoot = (drawingno: string) => {
      postMessage({
        type: 'show_root'
      });
    }

    window.App.liveview.toDevice.showDesign = (drawingno: string) => {
      postMessage({
        type: 'show_design'
      });
    }
  }, [htmlPreviewRef.current])

  const reloadIframe = useCallback(() => {
    htmlPreviewRef.current?.contentWindow?.postMessage({
      type: 'reload',
      isLiveView: true
    }, '*')
  }, [htmlPreviewRef.current])

  const value = useMemo<PreviewerContext>(() => ({
    htmlPreviewRef,
    previewUrl: htmlPreviewInfo?.previewUrl,
    previewHasCustomUrl: htmlPreviewInfo?.hasCustomUrl || false,
    setPreviewUrl,
    preview: {
      open: previewOpen,
      toggle: togglePreview,
      reload: reloadIframe
    },
    activeDrawingNo: activeDrawingNo,
    activeApplicationId: activeApplicationId
  }), [previewOpen, togglePreview, setPreviewUrl, htmlPreviewInfo])

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  )
}

export default PreviewerContextProvider
